import type { CommonData } from '@/app/common/createCommonData.ts';
import type { Localization } from '@/app/components/search/common/Localization.ts';
import type { DealCard } from '@/app/components/search/utils/buildDealCards/dealCardTypes.ts';
import { PROFILE_MANAGER_NAMESPACE_LONG } from '@lmn/nextjs-appfw/dist/casper/profileManager';
import { PROMOTION_TYPES } from '@/domainLogic/index.ts';
import type { ConfigValuesApp } from '@/app/common/config/configuration/configurationTypes.ts';
import type { CasperResponseProperties } from '@lmn/nextjs-appfw/dist/casper'; // import from @lmn/nextjs-appfw doesn't work for some reason, so we import from dist path directly

enum LOYALTY_TIERS {
  LEVEL_1 = 1,
  LEVEL_2 = 2,
  LEVEL_3 = 3,
}

export const getIsEmployee = (commonData: CommonData): boolean => !!commonData.authResponse?.isEmployee;

// get URN code
export const getLoyaltyCode = (casperResponseProperties?: CasperResponseProperties) =>
  // for testing if you need a URN code, you can check here:
  // https://lmn-hdp-configuration-panel.s3.amazonaws.com/internal/production/loyalty_global_catalog.json
  // @ts-ignore
  casperResponseProperties?.[PROFILE_MANAGER_NAMESPACE_LONG]?.pN;

// is Loyalty enabled
export const getIsLoyaltyEnabled = (configValuesApp?: ConfigValuesApp): boolean =>
  configValuesApp?.loyaltyEnabled ?? false;

// Loyalty tier
export const getUserLoyaltyTier = (commonData: CommonData): LOYALTY_TIERS | null => {
  const urn = getLoyaltyCode(commonData.casperResponseProperties);
  if (!urn) return null;

  const levels = Object.values(LOYALTY_TIERS) as LOYALTY_TIERS[];
  const level = levels.find((lvl) => urn.includes(`level_${lvl}`));
  return level ?? null;
};

// is enrolled in Loyalty
export const isLoyaltyUser = (commonData: CommonData): boolean => !!getUserLoyaltyTier(commonData);

// is Loyalty deal
export const isLoyaltyDeal = (dealCard: DealCard) => dealCard.product.rate.flags.loyalty;

export const hasLoyaltyPromotion = (promotionTypes: string[] | null): boolean => {
  if (!promotionTypes) return false;
  return promotionTypes.includes(PROMOTION_TYPES.LOYALTY);
};

export const shouldShowLoyaltyBanner = (
  commonData: CommonData,
  localization: Localization,
  isApp: boolean
): boolean => {
  const isLoyaltyEnabled = getIsLoyaltyEnabled(localization.configValuesApp);
  const isEnrolled = isLoyaltyUser(commonData);
  const isEmployee = getIsEmployee(commonData);

  return isLoyaltyEnabled && !isEnrolled && !isEmployee && !isApp;
};

export const shouldShowLoyaltyEnrolledBanner = (
  commonData: CommonData,
  localization: Localization,
  hasLoyaltyOffers: boolean,
  isApp: boolean
): boolean => {
  const isLoyaltyEnabled = getIsLoyaltyEnabled(localization.configValuesApp);
  const isEnrolled = isLoyaltyUser(commonData);
  const isEmployee = getIsEmployee(commonData);

  return isLoyaltyEnabled && isEnrolled && hasLoyaltyOffers && !isEmployee && !isApp;
};
